
import React, { useState, useEffect } from 'react';
import useAuthStore from '../stores/useAuthStore';
import DashboardLayout from '../components/DashboardLayout';
import axiosClient from '../services/api';
import { useToast } from '../components/ToastContainer';

const InstructorPartnerDashboard = () => {
    const { user, fetchProfile } = useAuthStore();
    const toast = useToast();
    const [formData, setFormData] = useState({
        institutionName: '',
        institutionLogo: null,
        signature: null,
        signatureName: ''
    });
    const [logoPreview, setLogoPreview] = useState(null);
    const [signaturePreview, setSignaturePreview] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [documentFiles, setDocumentFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);

    const [resubmitModalOpen, setResubmitModalOpen] = useState(false);
    const [resubmitFiles, setResubmitFiles] = useState([]);
    const [resubmitMessage, setResubmitMessage] = useState('');
    const [isResubmitting, setIsResubmitting] = useState(false);

    useEffect(() => {
        if (user?.partnerDetails) {
            setFormData({
                institutionName: user.partnerDetails.institutionName || '',
                institutionLogo: null,
                signature: null,
                signatureName: user.partnerDetails.signatureName || ''
            });
            if (user.partnerDetails.institutionLogo) {
                const fullUrl = user.partnerDetails.institutionLogo.startsWith('http')
                    ? user.partnerDetails.institutionLogo
                    : `${process.env.REACT_APP_SOCKET_URL}/public${user.partnerDetails.institutionLogo}`;

                setLogoPreview(`${fullUrl}?t=${Date.now()}`);
            }
            if (user.partnerDetails.signature) {
                const fullUrl = user.partnerDetails.signature.startsWith('http')
                    ? user.partnerDetails.signature
                    : `${process.env.REACT_APP_SOCKET_URL}/public${user.partnerDetails.signature}`;

                setSignaturePreview(`${fullUrl}?t=${Date.now()}`);
            }
        }
    }, [user]);

    const handleFileChange = (field, file) => {
        setFormData({ ...formData, [field]: file });
        const url = URL.createObjectURL(file);
        if (field === 'institutionLogo') setLogoPreview(url);
        if (field === 'signature') setSignaturePreview(url);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const payload = new FormData();
            payload.append('institutionName', formData.institutionName);
            payload.append('signatureName', formData.signatureName);
            if (formData.institutionLogo) payload.append('institutionLogo', formData.institutionLogo);
            if (formData.signature) payload.append('signature', formData.signature);

            await axiosClient.put('/partners/details', payload, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            fetchProfile();
            toast.success('Partner details updated');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to update');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUploadDocuments = async () => {
        if (!documentFiles.length) {
            toast.error('Please select at least one document to upload.');
            return;
        }

        if (documentFiles.length > 5) {
            toast.error('You can only upload a maximum of 5 documents.');
            return;
        }

        setIsUploading(true);

        try {
            const formData = new FormData();
            documentFiles.forEach(file => formData.append('documents', file));

            await axiosClient.post('/partners/upload-documents', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                maxBodyLength: Infinity,
            });

            toast.success('Documents submitted successfully. Admin will review shortly.');
            await fetchProfile();
        } catch (err) {
            console.error('Upload error:', err);
            toast.error(err.response?.data?.error || 'Document upload failed. Please try again.');
        } finally {
            setIsUploading(false);
            setDocumentFiles([]);
        }
    };

    const handleResubmit = async () => {
        if (!resubmitFiles.length) {
            toast.error('Please select at least one document');
            return;
        }

        if (resubmitFiles.length > 5) {
            toast.error('You can only upload a maximum of 5 documents.');
            return;
        }

        setIsResubmitting(true);
        try {
            const formData = new FormData();
            resubmitFiles.forEach(file => formData.append('documents', file));
            if (resubmitMessage) formData.append('customMessage', resubmitMessage);

            await axiosClient.post('/partners/resubmit-documents', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            toast.success('Documents resubmitted! Admin will review shortly.');
            setResubmitModalOpen(false);
            setResubmitFiles([]);
            setResubmitMessage('');

            await fetchProfile();

        } catch (err) {
            toast.error(err.response?.data?.error || 'Resubmission failed');
        } finally {
            setIsResubmitting(false);
        }
    };


    const applyForPartnership = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await axiosClient.post('/partners/apply', { institutionName: formData.institutionName });
            fetchProfile()
            toast.success('Partner Program Applied successfully!');
        } catch (err) {
            alert('Failed to apply');
        } finally {
            setIsSubmitting(false);
        }
    };


    if (user.partnerStatus === 'not_applied') {
        return (
            <DashboardLayout userRole="instructor">
                <div className="max-w-4xl mx-auto px-4 py-8">
                    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-8 text-center">
                        <div className="text-5xl mb-4">🤝</div>
                        <h1 className="text-2xl font-bold text-neutral-900 dark:text-white mb-4">Join the Partner Program</h1>
                        <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                            Become an institutional partner and earn as hign as 40-60% commissions with branded certificates.
                        </p>

                        <div className='mb-3'>
                            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                Institution Name *
                            </label>
                            <input
                                type="text"
                                value={formData.institutionName}
                                onChange={(e) => setFormData({ ...formData, institutionName: e.target.value })}
                                className="px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                required
                            />
                        </div>

                        <button
                            onClick={applyForPartnership}
                            disabled={isSubmitting}
                            className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm"
                        >
                            {isSubmitting ? 'Applying...' : 'Apply Now'}
                        </button>
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout userRole={user.role}>
            <div className="max-w-5xl mx-auto px-4 py-8">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-neutral-900 dark:text-white">Partner Program</h1>
                    <p className="text-neutral-600 dark:text-neutral-400 mt-2">
                        Manage your institution’s branding and program settings.
                    </p>
                </div>

                <div className="mb-8 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <div className="flex items-start">
                        <div className="flex-shrink-0">
                            <span className="text-blue-500 dark:text-blue-400">ℹ️</span>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">Program Status</h3>
                            <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                                {user.partnerStatus === 'approved' ? (
                                    <span className="font-medium">✅ Approved</span>
                                ) : ['document_required', 'documents_submitted', 'documents_resubmitted'].includes(user.partnerStatus) ? (
                                    <span className="font-medium">⏳ Document Review Phase</span>
                                ) : user.partnerStatus === 'pending' ? (
                                    <span className="font-medium">⏳ Pending Review</span>
                                ) : user.partnerStatus === 'rejected' ? (
                                    <span className="font-medium text-red-500">❌ Rejected</span>
                                ) : (
                                    <span className="font-medium">⏳ Pending Review</span>
                                )}

                                {user.partnerStatus === 'approved' && (
                                    <p className="mt-1">Your courses will display your institution logo when added to the Partner Program.</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {user.partnerStatus === 'rejected' && (
                    <div className="mt-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                        <div className="flex items-start">
                            <span className="text-yellow-500 dark:text-yellow-400 mt-0.5 mr-3">⚠️</span>
                            <div>
                                <h3 className="font-medium text-yellow-800 dark:text-yellow-200">Application Rejected</h3>
                                <p className="text-yellow-700 dark:text-yellow-300 text-sm mt-1">
                                    {user.partnerRejectionReason || 'No reason provided.'}
                                </p>
                                <button
                                    onClick={() => setResubmitModalOpen(true)}
                                    className="mt-3 text-sm font-medium text-primary-500 dark:text-primary-300 hover:underline"
                                >
                                    Resubmit Documents
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {user.partnerStatus === 'document_required' && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mt-6">
                        <h3 className="font-semibold text-blue-800 dark:text-blue-200 mb-3">📄 Submit Supporting Documents</h3>
                        <p className="text-red-700 dark:text-red-300 text-sm mb-4 italic">* please check your emails for further details of the documents your are to submit or update</p>
                        <p className="text-blue-700 dark:text-blue-300 text-sm mb-4">
                            Upload required documents (e.g., business registration, tax ID). Accepted formats: PDF, JPG, PNG (max 10MB each).
                        </p>
                        <div className="flex items-center gap-4">
                            <input
                                type="file"
                                multiple
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={(e) => setDocumentFiles(Array.from(e.target.files))}
                                className="px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                            <button
                                onClick={handleUploadDocuments}
                                disabled={!documentFiles.length || isUploading}
                                className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm rounded disabled:opacity-50"
                            >
                                {isUploading ? 'Uploading...' : 'Submit Documents'}
                            </button>
                        </div>
                    </div>
                )}

                {user.partnerStatus === 'approved' && (
                    <div className="bg-white dark:bg-neutral-800 p-6 rounded-lg">
                        <h2 className="text-xl font-semibold mb-4 dark:text-white">
                            Manage Partnership
                        </h2>

                        {user.partnerStatus === 'approved' && (<>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                                    <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-4">Institution Details</h2>
                                    <form onSubmit={handleSubmit} className="space-y-6">
                                        <div>
                                            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                                Institution Name *
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.institutionName}
                                                onChange={(e) => setFormData({ ...formData, institutionName: e.target.value })}
                                                className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                required
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                                Signature Name (for certificates) *
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.signatureName}
                                                onChange={(e) => setFormData({ ...formData, signatureName: e.target.value })}
                                                className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                required
                                            />
                                        </div>

                                        <div className="flex justify-end">
                                            <button
                                                type="submit"
                                                disabled={isSubmitting}
                                                className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm disabled:opacity-50"
                                            >
                                                {isSubmitting ? 'Saving...' : 'Save Details'}
                                            </button>
                                        </div>
                                    </form>
                                </div>

                                <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                                    <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-3">Branding</h2>
                                    <p className='text-sm dark:text-primary-300 mb-3 italic text-primary-500'>This is what will be shown in the certificate the partner program</p>

                                    <div className="mb-6">
                                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                            Institution Logo (PNG, max 2MB)
                                        </label>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => e.target.files[0] && handleFileChange('institutionLogo', e.target.files[0])}
                                            className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        />
                                        {logoPreview && (
                                            <div className="mt-2">
                                                <img
                                                    src={logoPreview}
                                                    alt="Institution Logo"
                                                    className="h-16 object-contain border border-neutral-200 dark:border-neutral-700 rounded"
                                                />
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                            Signature Image (Transparent PNG preferred)
                                        </label>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => e.target.files[0] && handleFileChange('signature', e.target.files[0])}
                                            className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        />
                                        {signaturePreview && (
                                            <div className="mt-2">
                                                <img
                                                    src={signaturePreview}
                                                    alt="Signature"
                                                    className="h-10 object-contain border border-neutral-200 dark:border-neutral-700 rounded"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </>)}
                    </div>
                )}

                {/* Resubmit Documents Modal */}
                {resubmitModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg w-full max-w-md">
                            <div className="p-6 border-b border-neutral-200 dark:border-neutral-700">
                                <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                                    Resubmit Partner Documents
                                </h3>
                            </div>
                            <div className="p-6 space-y-4">
                                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                                    Upload corrected or additional documents. Your application will be re-reviewed.
                                </p>
                                <input
                                    type="file"
                                    multiple
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    onChange={(e) => setResubmitFiles(Array.from(e.target.files))}
                                    className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                />
                                <textarea
                                    value={resubmitMessage}
                                    onChange={(e) => setResubmitMessage(e.target.value)}
                                    placeholder="Optional: Explain changes or corrections..."
                                    rows="2"
                                    className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                />
                            </div>
                            <div className="p-6 border-t border-neutral-200 dark:border-neutral-700 flex justify-end gap-2">
                                <button
                                    onClick={() => setResubmitModalOpen(false)}
                                    className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-neutral-200 hover:bg-neutral-300 dark:bg-neutral-700 dark:hover:bg-neutral-600 rounded-lg"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleResubmit}
                                    disabled={isResubmitting}
                                    className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg"
                                >
                                    {isResubmitting ? 'Submitting...' : 'Resubmit'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}


            </div>
        </DashboardLayout>
    );
};

export default InstructorPartnerDashboard;